/**
 * @file ImportDropZone.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-09-29
 * @description The "give me a document to read" half of an import modal: drop
 *              a file, click to choose one, paste a screenshot anywhere in the
 *              window, or paste text into the box (or anywhere outside a field).
 *              Shared by the price-list and haulage-rate-card imports, which
 *              differ only in what they then do with the read.
 *
 *              Reads nothing itself — it hands the File or the text to
 *              onFile / onText and shows `busy` while the host reads it.
 */

import React, { useEffect, useRef } from 'react';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import './ImportDropZone.css';

export const IMPORT_ACCEPTED_TYPES = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv', 'text/plain'
];
// Checked on extension too: some file managers send an empty or generic type.
const ACCEPTED_EXTENSIONS = /\.(png|jpe?g|gif|webp|pdf|xlsx?|docx|csv|txt)$/i;
export const isImportableFile = (f) => !!f
  && (IMPORT_ACCEPTED_TYPES.includes(f.type) || ACCEPTED_EXTENSIONS.test(f.name || ''));

const isEditable = (el) => !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
const UNREADABLE = 'That file type can\'t be read. Use an image, PDF, spreadsheet, Word document or CSV.';

function ImportDropZone({
  busy = false,
  error = '',
  preview = null,
  pasteText,
  setPasteText,
  onFile,
  onText,
  onError,
  title = 'Drop a file here, or click to choose one',
  busyTitle = 'Reading…',
  busyHint = 'This usually takes a few seconds.',
  pasteLabel = 'Or paste it as text',
  pastePlaceholder = ''
}) {
  const fileRef = useRef(null);
  useSlideOutButton([busy, pasteText.length > 0]);

  const take = (file) => {
    if (!file) return;
    if (!isImportableFile(file)) { onError?.(UNREADABLE); return; }
    onFile(file);
  };

  // Paste is wired to the whole window while waiting: an image is read straight
  // away; text pasted outside a field lands in the box and is read, since that
  // is what anyone pasting a block of rows here wants.
  useEffect(() => {
    if (busy) return undefined;
    const onPaste = (e) => {
      const image = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith('image/'));
      if (image) {
        e.preventDefault();
        onFile(image.getAsFile());
        return;
      }
      if (isEditable(e.target)) return;
      const text = e.clipboardData?.getData('text/plain');
      if (text && text.trim()) {
        e.preventDefault();
        setPasteText(text);
        onText(text);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [busy, onFile, onText, setPasteText]);

  return (
    <div className="import-drop-body">
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,.pdf,.xlsx,.xls,.docx,.csv,.txt,text/csv"
        style={{ display: 'none' }}
        onChange={(e) => { take(e.target.files?.[0]); e.target.value = ''; }}
      />

      {error && <div className="import-drop-error">{error}</div>}

      <div
        className={`import-drop${busy ? ' busy' : ''}`}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('over'); }}
        onDragLeave={(e) => e.currentTarget.classList.remove('over')}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove('over');
          const file = [...(e.dataTransfer?.files || [])].find(isImportableFile);
          if (file) onFile(file); else onError?.(UNREADABLE);
        }}
        onClick={busy ? undefined : () => fileRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (!busy && (e.key === 'Enter' || e.key === ' ')) fileRef.current?.click(); }}
      >
        {busy ? (
          <>
            <span className="material-symbols-rounded spinning">progress_activity</span>
            <span className="import-drop-title">{busyTitle}</span>
            <span className="import-drop-hint">{busyHint}</span>
          </>
        ) : (
          <>
            <span className="material-symbols-rounded">upload_file</span>
            <span className="import-drop-title">{title}</span>
            <span className="import-drop-hint">
              A screenshot, PDF, spreadsheet, Word document or CSV, up to 20 MB. You can also
              paste a screenshot — <kbd>Cmd</kbd>/<kbd>Ctrl</kbd> + <kbd>V</kbd> — anywhere in this
              window. Nothing is stored.
            </span>
          </>
        )}
      </div>

      {preview && (
        <div className="import-drop-preview">
          <img src={preview} alt="What is being read" />
        </div>
      )}

      {!busy && (
        <div className="import-drop-paste">
          <label htmlFor="import-drop-paste-text">{pasteLabel}</label>
          <Textarea
            id="import-drop-paste-text"
            rows={6}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={pastePlaceholder}
          />
          <div className="buttons slideout">
            <div>
              <button className="btn-slideout" onClick={() => onText(pasteText)} disabled={!pasteText.trim()}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">document_scanner</span></div>
                <span className="btn-label">Read pasted text</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportDropZone;
